import { Button, VStack, Text, Flex } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ReactElement } from 'react';
import { ModalClassGuideRemovalVariantProps, SharedModalProps } from 'types/Modal';
import { useRemoveGuides } from 'hooks/api/backend/managament/getClasses';
import { useQueryClient } from '@tanstack/react-query';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { toastContent } from 'config/toast';
import * as Sentry from '@sentry/react';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalClassGuideRemovalVariantProps;

export const ClassGuideRemovalModal = ({
  handleClose,
  isShown,
  guideId,
  guideName,
  classId,
}: Props): ReactElement => {
  const queryClient = useQueryClient();
  const toast = useNovaToast();
  const { mutateAsync: removeGuides, isPending: isRemovingGuide } = useRemoveGuides();

  const handleRemove = async (): Promise<void> => {
    if (!guideId || !classId) return;
    try {
      const updatedClass = await removeGuides({ classId, ids: [guideId] });
      // Update cache with the response data instead of refetching
      queryClient.setQueryData(['class', classId], updatedClass);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['class-students-search', classId] }),
        queryClient.invalidateQueries({ queryKey: ['class-teachers-search', classId] }),
      ]);
      toast.show({
        description: 'Praxisanleitende erfolgreich entfernt',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" hideHeader size="md">
      <CustomModalContent p="30px">
        <Flex direction="column" gap="30px">
          <Flex direction="column" align="flex-end" gap="5px">
            <NovaIconButton
              aria-label="Schließen"
              variant="primary"
              size="l"
              onClick={handleClose}
              minW="24px"
              h="24px"
              p={0}
              icon={CloseIcon}
              iconProps={{ w: '24px', h: '24px', color: 'text.primary' }}
            />
            <VStack gap="15px" align="flex-start" w="full">
              <Text textStyle="h4.medium" color="text.primary">
                Praxisanleitende entfernen
              </Text>
              <Text textStyle="body.m.regular" color="text.primary">
                Bist du sicher, dass du die Praxisanleitung {guideName} aus dem Kurs entfernen
                möchtest?
              </Text>
            </VStack>
          </Flex>

          <Flex gap="l" justify="flex-end">
            <Button onClick={handleClose} variant="tertiary">
              Abbrechen
            </Button>
            <Button onClick={handleRemove} variant="destructive" loading={isRemovingGuide}>
              Entfernen
            </Button>
          </Flex>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
